import {
  Close as CloseIcon,
  CheckCircleOutline as SuccessIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import consultationReceptionService from "../../services/consultationReceptionService"; // Ajout import
import consultationService from "../../services/consultationService";
import receptionService from "../../services/receptionService";
import useAuthStore from "../../store/authStore";

const ReceptionModal = ({ open, onClose, assure }) => {
  const [loading, setLoading] = useState(false);
  const [commentaire, setCommentaire] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [consultations, setConsultations] = useState([]);
  const [selectedConsultation, setSelectedConsultation] = useState("");
  const [consultationsLoading, setConsultationsLoading] = useState(false);
  const user = useAuthStore((state) => state.user);

  // Charger les consultations de l'assuré à l'ouverture du modal
  useEffect(() => {
    if (open && assure?.id) {
      setConsultationsLoading(true);
      consultationService
        .getAllConsultations(1, 100, { assure_id: assure.id })
        .then((result) => {
          setConsultations(result.data || []);
        })
        .catch(() => setConsultations([]))
        .finally(() => setConsultationsLoading(false));
    }
    if (!open) {
      setSelectedConsultation("");
      setCommentaire("");
      setStatus({ type: "", message: "" });
    }
  }, [open, assure]);

  const handleClose = () => {
    setCommentaire("");
    setSelectedConsultation("");
    setStatus({ type: "", message: "" });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedConsultation) {
      setStatus({
        type: "warning",
        message: "Veuillez sélectionner une consultation à associer",
      });
      return;
    }

    if (!commentaire.trim()) {
      setStatus({
        type: "warning",
        message: "Veuillez saisir un commentaire pour la réception",
      });
      return;
    }

    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      const receptionData = {
        assure_id: assure.id,
        agent_clinique_id: user.id,
        commentaire: commentaire.trim(),
      };

      // Création de la réception
      const result = await receptionService.createReception(receptionData);

      if (result) {
        // Association consultation-réception
        await consultationReceptionService.createConsultationReception({
          consultation_id: selectedConsultation,
          reception_id: result.data.id,
        });

        setStatus({
          type: "success",
          message:
            "Réception et association consultation enregistrées avec succès",
        });
        toast.success("✅ Réception enregistrée et consultation associée");
        setTimeout(handleClose, 2000);
      } else {
        setStatus({
          type: "error",
          message: result.message || "Erreur lors de l'enregistrement",
        });
        if (result.error?.details) {
          const errors = Object.values(result.error.details);
          setStatus((prev) => ({
            ...prev,
            details: errors,
          }));
        }
      }
    } catch (error) {
      console.error("Erreur:", error);
      setStatus({
        type: "error",
        message: error.message || "Une erreur inattendue est survenue",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "primary.main",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">Nouvelle Réception</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body1" gutterBottom>
              <strong>Assuré:</strong> {assure?.fullName}
            </Typography>
            <Typography variant="body1">
              <strong>Matricule:</strong> {assure?.matricule}
            </Typography>
          </Box>

          {/* Sélection de la consultation */}
          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel id="consultation-select-label">
              Consultation à associer
            </InputLabel>
            <Select
              labelId="consultation-select-label"
              value={selectedConsultation}
              label="Consultation à associer"
              onChange={(e) => setSelectedConsultation(e.target.value)}
              disabled={consultationsLoading || loading}
            >
              {consultationsLoading && (
                <MenuItem value="">
                  <em>Chargement...</em>
                </MenuItem>
              )}
              {!consultationsLoading && consultations.length === 0 && (
                <MenuItem value="">
                  <em>Aucune consultation disponible</em>
                </MenuItem>
              )}
              {!consultationsLoading &&
                consultations.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label || c.description || `Consultation #${c.id}`}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            required
            fullWidth
            label="Commentaire de réception"
            multiline
            rows={4}
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            variant="outlined"
            error={!commentaire.trim()}
            helperText={
              !commentaire.trim() ? "Veuillez saisir un commentaire" : ""
            }
            disabled={loading}
            sx={{ mb: 2 }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              handleClose();
              toast.info("Action annulée");
            }}
            variant="outlined"
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !commentaire.trim() || !selectedConsultation}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SuccessIcon />
              )
            }
          >
            {loading ? "Enregistrement..." : "Confirmer la réception"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReceptionModal;
